import { put, call, takeLatest, all } from 'redux-saga/effects';
import { replace } from 'connected-react-router';
import Bugsnag from '@bugsnag/js';
import { v4 as uuidv4 } from 'uuid';
import { processRequest } from '../../services/Api';
import { PACKAGES_PATH, COOKIES } from '../../services/Constants';
import { IS_MOBILE_APP } from '../../services/capacitor/Constants';
import {
	getCookie,
	getLocalStorageItem,
	removeCookie,
	removeLocalStorageItem,
	setLocalStorageItem,
} from '../../services/Helper';
import { historyPush, historyReplace } from '../../services/History';
import { sidebarActionTypes } from './Constants';
import {
	removeActivationCodeCookie,
	removeLicenseCodeCookie,
} from '../../pages/activation/Constants';
import { removeAccessToken } from '../../services/AccessToken';
import * as sidebarActions from './Actions';
import * as packagesActions from '../../pages/packages/Actions';
import * as signUpActions from '../../pages/signUp/Actions';
import * as pushNotificationActions from '../appPushNotification/Actions';
import * as errorBoundaryActions from '../errorBoundary/Actions';
import * as errorHandlerActions from '../errorHandler/Actions';
import * as tutorChatActions from '../modals/chat/Actions';

export default function* () {
	yield all([
		yield takeLatest(sidebarActionTypes.GET_USER_DATA_REQUEST, handleUserDataRequest),
		yield takeLatest(sidebarActionTypes.LOGOUT, handleUserLogout),
		yield takeLatest(sidebarActionTypes.GET_COUNTRIES_REQUEST, handleGetCountriesRequest),
		yield takeLatest(sidebarActionTypes.GET_MENU_MADE_LISTS_REQUEST, handleGetMadeListRequest),
	]);
}

export function* handleUserDataRequest({ payload }) {
	try {
		const { navigateFromPackagesPage, navigateFromSignUpPage } = payload || {};
		const { data } = yield call(processRequest, 'get_user_data');
		const {
			id,
			needs_to_fill_education_details,
			needs_to_fill_personal_info_details,
			needs_to_choose_package,
			needs_to_choose_referrer,
			needs_to_verify_email,
			show_parent_details_offer,
			show_reverse_trial_offer,
		} = data;
		Bugsnag.setUser(id);
		const activationCode = getCookie(COOKIES.activationCode);

		yield put(sidebarActions.getUseDataSuccess(data));
		const { search, pathname } = window.location;

		if (needs_to_fill_personal_info_details) {
			return yield call(historyReplace, '/update-personal-info');
		}

		if (!IS_MOBILE_APP) {
			const query = search;
			const location = query ? `${pathname}${query}` : pathname;
			if (
				!needs_to_fill_education_details &&
				!needs_to_choose_package &&
				!needs_to_choose_referrer &&
				location === PACKAGES_PATH
			) {
				return yield call(historyReplace, '/my-packages');
			}
		}

		if (needs_to_fill_education_details && !navigateFromSignUpPage) {
			return yield call(historyReplace, '/update-education-details');
		}

		if (!needs_to_fill_education_details && needs_to_choose_referrer) {
			return yield call(historyReplace, '/referrer-details');
		}

		if (!needs_to_fill_education_details && needs_to_verify_email) {
			return yield call(historyReplace, '/verify-email');
		}

		if (
			needs_to_choose_package &&
			!needs_to_choose_referrer &&
			!navigateFromPackagesPage &&
			!navigateFromSignUpPage &&
			!pathname.includes('confirm-package') &&
			!pathname.includes('activeren') &&
			typeof activationCode === 'undefined'
		) {
			return yield call(historyReplace, '/packages');
		}
		if (show_parent_details_offer) {
			setLocalStorageItem('showParentDetailsOffer', true);
			return yield call(historyPush, `/offer/${show_parent_details_offer}`);
		}
		if (show_reverse_trial_offer) {
			return yield call(historyPush, '/offer/reverse-trial');
		}
	} catch (e) {
		const {
			response: { status },
		} = e;
		if (status !== 401) {
			yield put(errorHandlerActions.handleError(e, 'full-blocking'));
			yield put(sidebarActions.getUseDataError());
			yield put(errorBoundaryActions.setError(e));
		}
	}
}

export function* handleUserLogout({ payload }) {
	try {
		const { route, shouldDeleteActivationCode } = payload;
		const deviceTokenStatus = getLocalStorageItem('deviceTokenStatus');
		const deviceToken = getLocalStorageItem('deviceToken');

		removeLocalStorageItem('jwt-token');
		removeCookie(COOKIES.chatAccessToken);

		if (IS_MOBILE_APP && deviceTokenStatus === 'registered') {
			yield put(
				pushNotificationActions.deregisterPushNotificationForDeviceRequest(
					deviceToken,
					'shouldRestore',
				),
			);
		}
		yield call(processRequest, 'auth/invalidate_token', 'POST');
		yield call(removeAccessToken);
		if (shouldDeleteActivationCode) {
			removeLocalStorageItem('activationCode');
			removeLocalStorageItem('license');
			yield call(removeActivationCodeCookie);
			yield call(removeLicenseCodeCookie);
		}
		yield put(sidebarActions.clearUserData());
		yield put(packagesActions.cleanPackagesState());
		yield put(signUpActions.cleanSignUpState());
		yield put(tutorChatActions.clearAiChatState());
		if (route) {
			yield put(replace(route));
		} else {
			yield call(historyReplace, '/sign-in');
		}
		const uuid = uuidv4();
		setLocalStorageItem('deviceId', uuid);
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'ignore'));
		yield put(sidebarActions.getUseDataError());
	}
}

export function* handleGetCountriesRequest() {
	try {
		const { data } = yield call(processRequest, `countries`);
		yield put(sidebarActions.getCountriesSuccess(data.countries));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'ignore'));
		yield put(sidebarActions.getCountriesError());
	}
}

export function* handleGetMadeListRequest() {
	try {
		const { data } = yield call(processRequest, 'practiceable_items/created', 'GET');

		yield put(sidebarActions.getMadeListsSuccess(data.created_items));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(sidebarActions.getMadeListsError());
	}
}
